import React from 'react';
import { TFunction, useTranslation, withTranslation, WithTranslation } from 'next-i18next';
import { Ref } from 'react-hook-form';
import styled from 'styled-components';
import PageAlert from '~/components/common/page-alerts';
import { spacing, TextParagraph } from '~/theme';
import { buildErrorMsgInterpolationOptions } from '~/utils/helpers';
import sanitizeHtml from '~/utils/sanitize-html';

const StyledAlert = styled(PageAlert)`
    margin-top: ${spacing.spacing0};
`;

const StyledList = styled.ul`
    li ul {
        margin-top: ${spacing.spacing3};
        margin-bottom: ${spacing.spacing3};
    }

    li ul li {
        padding-bottom: 0px;
    }

    li p {
        margin-top: ${spacing.spacing3};
    }
`;

export interface PageError {
    errorCode: string;
    formElementRef?: Ref | HTMLElement;
}

export interface PageErrorProps extends WithTranslation {
    t: TFunction;
    heading?: string | null;
    leadTextCode?: string;
    showLeadText?: boolean;
    errors?: PageError[] | PageError;
    className?: string;
}

interface ErrorElementProps {
    errorCode: string;
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

type ErrorTranslationValue =
    | string
    | {
          heading?: string;
          message?: string;
      };

const getErrorMessage = (translatedValue: ErrorTranslationValue): string => {
    if (typeof translatedValue === 'string') {
        return translatedValue;
    }

    return translatedValue?.message ?? '';
};

const getPrimaryErrorCode = (errorCode?: string): string => errorCode?.split(',')[0]?.trim() ?? '';
const isEmptyPageError = (error?: PageError): boolean =>
    !error || !getPrimaryErrorCode(error.errorCode);

const getTranslatedErrorValue = (
    t: TFunction,
    code: string,
    fallback: string,
    errorCode: string
): ErrorTranslationValue => {
    const translatedValue = t(`errors:errorCodes.${code}`, {
        ...buildErrorMsgInterpolationOptions(errorCode),
        defaultValue: fallback,
        returnObjects: true,
    });

    if (typeof translatedValue === 'string') {
        return translatedValue;
    }

    if (translatedValue && typeof translatedValue === 'object') {
        return translatedValue as ErrorTranslationValue;
    }

    return fallback;
};

const ErrorElement = ({ errorCode, onClick }: ErrorElementProps) => {
    const { t } = useTranslation(['common', 'errors']);
    const code = getPrimaryErrorCode(errorCode);
    const fallbackMessage = '';
    const translatedValue = getTranslatedErrorValue(t, code, fallbackMessage, errorCode);
    const translatedText = getErrorMessage(translatedValue) || fallbackMessage;
    return (
        <>
            {onClick ? (
                <a
                    href="#"
                    onClick={onClick}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(translatedText, {
                            ALLOWED_ATTR: ['target', 'href'],
                        }),
                    }}
                />
            ) : (
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(translatedText, {
                            ALLOWED_ATTR: ['target', 'href'],
                        }),
                    }}
                />
            )}
        </>
    );
};

const PageErrors = ({
    t,
    errors,
    heading,
    leadTextCode,
    showLeadText = true,
    className,
}: PageErrorProps) => {
    if (
        !errors ||
        (Array.isArray(errors) && errors.length === 0) ||
        (!Array.isArray(errors) && isEmptyPageError(errors))
    ) {
        return null;
    }

    const hasVisibleMessage = (error?: PageError): boolean => {
        if (!error || isEmptyPageError(error)) {
            return false;
        }

        const code = getPrimaryErrorCode(error.errorCode);
        const translatedValue = getTranslatedErrorValue(t, code, '', error.errorCode);
        const translatedText = getErrorMessage(translatedValue);
        return translatedText.trim().length > 0;
    };

    const displayReadyErrors = Array.isArray(errors)
        ? errors.filter(error => hasVisibleMessage(error))
        : errors;

    if (
        (Array.isArray(displayReadyErrors) && displayReadyErrors.length === 0) ||
        (!Array.isArray(displayReadyErrors) && !hasVisibleMessage(displayReadyErrors))
    ) {
        return null;
    }

    const handleItemClick = (elementRef: HTMLElement) => (event: React.MouseEvent<HTMLElement>) => {
        if (!elementRef) {
            return;
        }

        if (event) {
            event.preventDefault();
        }

        const elementToScroll = elementRef.id.match(/year|month|day/i)
            ? (elementRef?.parentElement?.parentElement?.parentElement ?? elementRef)
            : (elementRef?.parentElement ?? elementRef);
        elementToScroll?.scrollIntoView({
            behavior: 'smooth',
        });
        setTimeout(() => elementRef?.focus(), 1000);
    };

    return (
        <>
            {displayReadyErrors && (
                <StyledAlert
                    alertType="error"
                    heading={heading ?? t('errors:errorHeadings:problem')}
                    className={className}
                >
                    {showLeadText && (
                        <TextParagraph>
                            {t(
                                `errors:errorHeadings:${leadTextCode ?? (Array.isArray(displayReadyErrors) && displayReadyErrors?.length > 1 ? 'errorsFoundOnthisPage' : 'errorFoundOnthisPage')}`
                            )}
                        </TextParagraph>
                    )}
                    {Array.isArray(displayReadyErrors) && displayReadyErrors?.length > 0 ? (
                        <StyledList>
                            {displayReadyErrors?.map((error, index) => (
                                <li key={`error.errorCode-${index}`}>
                                    <ErrorElement
                                        errorCode={error?.errorCode}
                                        onClick={
                                            error.formElementRef
                                                ? handleItemClick(
                                                      error?.formElementRef as HTMLElement
                                                  )
                                                : undefined
                                        }
                                    />
                                </li>
                            ))}
                        </StyledList>
                    ) : (
                        <TextParagraph>
                            <ErrorElement
                                errorCode={(displayReadyErrors as PageError)?.errorCode}
                                onClick={
                                    (displayReadyErrors as PageError).formElementRef
                                        ? handleItemClick(
                                              (displayReadyErrors as PageError)
                                                  ?.formElementRef as HTMLElement
                                          )
                                        : undefined
                                }
                            />
                        </TextParagraph>
                    )}
                </StyledAlert>
            )}
        </>
    );
};

export default withTranslation(['common', 'errors'])(PageErrors);
